import React, { createContext, useReducer, Dispatch, useEffect } from 'react'
import notifications from '../core/notifications'
import { DataField } from '../util/data-field'
import { GenericButtonProps } from '../components/GenericButton'

export interface UpdateWorkflowStateAction {
  type:
    | 'set'
    | 'setData'
    | 'goBack'
    | 'nextStep'
    | 'clearSavedState'
    | 'restoreSavedState'
  value: any
}

export interface WorkflowStepValidationResult {
  valid: boolean
  message: string
}

export interface WorkflowStep {
  backDisabled?: boolean
  validate?: (data?: any) => WorkflowStepValidationResult
  customComponent?: any
  fields?: DataField[] | ((data: any) => DataField[])
  title?: any
  subtext?: any
  footnote?: any
  button?: GenericButtonProps
  id: string
  getNextStepId?: (data: any) => WorkflowHistory
}

export interface WorkflowHistory {
  id: string
  track?: string
}

export interface WorkflowTrack {
  id: string
  steps: WorkflowStep[]
}

export interface WorkflowState {
  steps: WorkflowStep[]
  main: WorkflowStep[]
  tracks?: WorkflowTrack[]
  data: { [key: string]: any }
  currentStep?: WorkflowStep
  history: WorkflowHistory[]
  currentTrack?: string
  canGoBack: boolean
  transitionKey?: string
  autosave?: boolean
  id: string
  hasSavedState: boolean
  complete: boolean
}

function getStep(
  state: WorkflowState,
  id: string | null | undefined,
  track: string | null | undefined
) {
  const steps = track
    ? state.tracks?.find((it) => it.id === track)?.steps
    : state.main

  if (!steps) {
    return null
  }

  const step = steps.find((it) => it.id === id)
  const index = step ? steps?.indexOf(step) : -1
  return {
    track,
    step,
    steps,
    index,
  }
}

function getPreviousStep(state: WorkflowState) {
  const { history } = state

  const previous = history.pop()
  if (!previous) {
    return undefined
  }

  const { id, track } = previous

  return getStep(state, id, track)
}

function getNextStep(state: WorkflowState) {
  const { currentStep } = state

  if (currentStep?.getNextStepId) {
    const { track, id } = currentStep.getNextStepId(state.data)
    return getStep(state, id, track)
  }

  const index = currentStep ? state.steps.indexOf(currentStep) : -1
  const next = state.steps[index + 1]?.id
  return getStep(state, next, state.currentTrack)
}

function getTransitionKey(id: string, track: string | null | undefined) {
  return [id, track ?? 'name'].join('-')
}

function getSavedStateKey(id: string) {
  return `wf_${id}`
}

function saveState(state: WorkflowState) {
  if (
    !state.autosave ||
    !Object.keys(state.data).length ||
    !state.history.length ||
    state.complete
  ) {
    return
  }

  const key = getSavedStateKey(state.id)
  const { history, data, currentStep, currentTrack } = state
  window.localStorage.setItem(
    key,
    JSON.stringify({
      history,
      data,
      step: currentStep?.id,
      track: currentTrack,
    })
  )
}

const reducer = (state: WorkflowState, action: UpdateWorkflowStateAction) => {
  const { currentStep, currentTrack, history } = state
  switch (action.type) {
    case 'set':
      return { ...state, ...action.value }
    case 'setData':
      return {
        ...state,
        data: { ...state.data, ...action.value },
      }
    case 'clearSavedState':
      window.localStorage.removeItem(getSavedStateKey(state.id))
      return {
        ...state,
        complete: action.value.complete,
        hasSavedState: false,
      }
    case 'restoreSavedState':
      const saved = JSON.parse(
        window.localStorage.getItem(getSavedStateKey(state.id)) ?? '{}'
      )

      const savedStep = getStep(state, saved.step, saved.track)

      return {
        ...state,
        data: saved.data,
        history: saved.history,
        currentStep: savedStep?.step,
        currentTrack: savedStep?.track,
        index: savedStep?.index,
        steps: savedStep?.steps,
        canGoBack: !!saved.history?.length,
        hasSavedState: false,
      }
    case 'goBack':
      if (!state.canGoBack) {
        return state
      }

      const previous = getPreviousStep(state)
      if (!previous || !previous.step) {
        return state
      }

      const { step, track, steps } = previous
      const canGoBack = previous && !step.backDisabled && history.length > 0

      return {
        ...state,
        canGoBack,
        history,
        steps,
        transitionKey: getTransitionKey(step.id, track),
        currentTrack: track,
        currentStep: step,
      }
    case 'nextStep':
      let validation: WorkflowStepValidationResult = {
        valid: true,
        message: '',
      }

      if (currentStep?.validate) {
        validation = currentStep.validate(state.data)
      } else if (currentStep?.fields) {
        const fields =
          typeof currentStep.fields === 'function'
            ? currentStep.fields(state.data)
            : currentStep.fields

        const required = fields.filter((it) => it.required === true)
        if (
          required.length > 0 &&
          !required.every((it) => !!state.data[it.key])
        ) {
          validation = {
            valid: false,
            message: 'Please fill out all of the required fields.',
          }
        }
      }

      if (!validation.valid) {
        notifications.toast(validation.message, 'Error')
        return state
      }

      const next = getNextStep(state)

      if (!next || !next.step) {
        return state
      }

      if (currentStep) {
        history.push({ id: currentStep.id, track: currentTrack })
      }

      return {
        ...state,
        history,
        transitionKey: getTransitionKey(next.step.id, next.track),
        canGoBack: history.length > 0 && !next.step.backDisabled,
        steps: next.steps,
        currentTrack: next.track,
        currentStep: next.step,
      }
  }
}

export interface WorkflowContextProps {
  dispatch: Dispatch<UpdateWorkflowStateAction>
  state: WorkflowState
}

const initial = {
  steps: [],
  data: {},
  canGoBack: false,
  history: [],
  main: [],
  id: '',
  hasSavedState: false,
  complete: false,
} as WorkflowState

const WorkflowContext = createContext<WorkflowContextProps>({
  state: initial,
  dispatch: () => undefined,
})

export interface WorkflowContextProviderProps {
  initialState?: Partial<WorkflowState>
  initialStepIndex?: number
}

const WorkflowContextProvider: React.FC<WorkflowContextProviderProps> = ({
  children,
  initialState = {},
  initialStepIndex = 0,
}) => {
  const fullInitialState = {
    ...initial,
    ...initialState,
  }

  if (initialState.autosave) {
    const saved = !!window.localStorage.getItem(
      getSavedStateKey(fullInitialState.id)
    )

    if (saved) {
      fullInitialState.hasSavedState = true
    }
  }

  fullInitialState.main = fullInitialState.steps
  fullInitialState.currentStep = fullInitialState.steps[initialStepIndex]

  let [state, dispatch] = useReducer(reducer, fullInitialState)
  let value = {
    state,
    dispatch,
  }

  useEffect(() => {
    saveState(state)
  }, [state])

  return (
    <WorkflowContext.Provider value={value}>
      {children}
    </WorkflowContext.Provider>
  )
}

let WorkflowContextConsumer = WorkflowContext.Consumer

export { WorkflowContext, WorkflowContextProvider, WorkflowContextConsumer }
